import { AlumniContentReferencesEntry } from "../schema/universal";

export function isUrlStage(host?: string) {
  const hostname = (host ?? "").toLowerCase().split(":")[0];
  if (!hostname) return false;
  return (
    hostname === "localhost" ||
    hostname === "hbsstg.org" ||
    hostname.endsWith(".hbsstg.org")
  );
}

export function toStageUrl(url: string) {
  if (url.startsWith("https://preview.alumni.hbs.edu/")) {
    return url.replace(
      "https://preview.alumni.hbs.edu/",
      "https://preview-alumni.webdev.int.host.aws.hbsstg.org/",
    );
  }
  if (url.startsWith("https://www.alumni.hbs.edu/")) {
    return url.replace(
      "https://www.alumni.hbs.edu/",
      "https://www-alumni.webdev.int.host.aws.hbsstg.org/",
    );
  }
  return url;
}

export function getAlumniEndpoints(
  type?: string,
  host?: string,
  alumniContentReferences?: AlumniContentReferencesEntry,
) {
  const outageRedirect = getOutageRedirect(alumniContentReferences);
  if (outageRedirect) {
    const appsToRedirect = alumniContentReferences?.fields.appToGetRedirected;
    // No apps listed means every app is redirected.
    if (!appsToRedirect?.length) return outageRedirect;

    const normalizedType = normalizeAppName(type);
    const isRedirected = appsToRedirect.some((app) => {
      const normalizedApp = normalizeAppName(app);
      return (
        normalizedApp === normalizedType ||
        // "Giving Donation Form" is served by the givingBaseUrl case.
        (normalizedApp === "givingdonationform" &&
          normalizedType === "givingbaseurl")
      );
    });
    if (isRedirected) return outageRedirect;
  }
  const isStage = isUrlStage(host);

  switch (type) {
    case "profileEditor":
      return getProfileEditor(isStage);
    case "editSubmitMyClassNotes":
      return getEditSubmitMyClassNotes(isStage);
    case "submitNoteToSecretary":
      return getSubmitNoteToSecretary(isStage);
    case "alumniMessaging":
      return getAlumniMessaging(isStage);
    case "emailMyClass":
      return getEmailMyClass(isStage);
    case "staffOutreach":
      return getStaffOutreach(isStage);
    case "donationTaxReceiptForm":
      return "https://alumni.harvard.edu/giving/tax-information/request-a-tax-receipt";
    case "givingBaseUrl":
      return getGivingBaseUrl(isStage);
    default:
      return "";
  }
}

export function getCedidServiceConfig() {
  return {
    endpointTest:
      "https://test.secure.cecredentialtrust.com:8086/v4/cecredentialvalidate",
    endpointLive:
      "https://secure.cecredentialtrust.com:8086/v4/cecredentialvalidate",
    clientId: "ed3c4418-6fff-4b6d-a693-f84bfce7d2ca",
  };
}

function normalizeAppName(name?: string) {
  return (name ?? "").toLowerCase().replace(/\s+/g, "");
}

function getOutageRedirect(
  alumniContentReferences?: AlumniContentReferencesEntry,
) {
  const redirect = alumniContentReferences?.fields.appFormRedirect;
  if (!redirect) return undefined;
  if (redirect.startsWith("/") && !redirect.startsWith("//")) {
    return redirect;
  }

  try {
    const url = new URL(redirect);
    return url.protocol === "http:" || url.protocol === "https:"
      ? redirect
      : undefined;
  } catch {
    return undefined;
  }
}

function getProfileEditor(isStage?: boolean) {
  if (isStage) {
    return "https://apps.hbsstg.org/alumniProfileEditor/";
  } else {
    return "https://apps.hbs.edu/alumniProfileEditor/";
  }
}

function getEditSubmitMyClassNotes(isStage?: boolean) {
  if (isStage) {
    return "https://secure-stage.hbsstg.org/alumniLifeClassNotesService/external/secretary/main.htm";
  } else {
    return "https://secure.hbs.edu/alumniLifeClassNotesService/external/secretary/main.htm";
  }
}

function getSubmitNoteToSecretary(isStage?: boolean) {
  if (isStage) {
    return "https://secure-stage.hbsstg.org/alumniLifeClassNotesService/external/alum/submitnote.htm";
  } else {
    return "https://secure.hbs.edu/alumniLifeClassNotesService/external/alum/submitnote.htm";
  }
}

function getEmailMyClass(isStage?: boolean) {
  if (isStage) {
    return "https://apps.hbsstg.org/alumniMessaging/";
  } else {
    return "https://apps.hbs.edu/alumniMessaging/";
  }
}

function getAlumniMessaging(isStage?: boolean) {
  if (isStage) {
    return "https://apps.hbsstg.org/alumniMessaging/";
  } else {
    return "https://apps.hbs.edu/alumniMessaging/";
  }
}

function getStaffOutreach(isStage?: boolean) {
  if (isStage) {
    return "https://apps.hbsstg.org/alumniProfileEditor/?view=staffoutreach";
  } else {
    return "https://apps.hbs.edu/alumniProfileEditor/?view=staffoutreach";
  }
}
function getGivingBaseUrl(isStage?: boolean) {
  if (isStage) {
    return "https://acetst.cadm.harvard.edu/give";
  } else {
    return "https://community.alumni.harvard.edu/give";
  }
}
